// Checkout Forms
import { formatCardNumberAmex, formatCardNumberDefault } from 'checkout-forms/form/normalizers/formatCardNumber';
import formatExpiryDate from 'checkout-forms/form/normalizers/formatExpiryDate';

// Config
import { CARD_NUMBER, CARD_NAME, CARD_EXPIRY_DATE, CARD_SECURITY_CODE } from './cardDetails';
import { getCvvClassName } from '../helpers/creditCardInputStyles';
import supportedCreditCardTypes from '@constants/supportedCreditCardTypes';
import { isClickAndCollectChoice } from '@utils/delivery/deliveryHelper';
import { deepEqual, isEmptyObject } from '@utils/object';

// Components
import { SecuredCardInputTooltip } from '@components/add-payment-details/card-details/billing-details';

export const SAVED_CARD_DETAILS_FORM_ID = 'savedCardDetailsForm';

export const cvvSkipVariants = {
  ALL_CLICK_AND_COLLECT: 'all_click_and_collect',
  NEW_CLICK_AND_COLLECT: 'new_click_and_collect',
  ALL_TRANSACTIONS: 'all_transactions',
};

export function canSkipCVV({
  cardType,
  selectedDeliveryChoiceId,
  selectedBillingAddress,
  confirmedDeliveryAddress,
  reduceSavedCardCvvSkipEnabled,
  reducedSavedCardCvvSkipVariant,
  isSelectedCollectionPointInSavedPoints,
  addressModified,
  billingAddressRecentlyUpdated,
  deliveryAddressRecentlyUpdated,
}) {
  if (cardType === supportedCreditCardTypes.AMERICAN_EXPRESS || addressModified) {
    return false;
  }

  // If either address was recently updated, require CVV
  if (billingAddressRecentlyUpdated || deliveryAddressRecentlyUpdated) {
    return false;
  }

  if (reduceSavedCardCvvSkipEnabled) {
    switch (reducedSavedCardCvvSkipVariant) {
      case cvvSkipVariants.ALL_CLICK_AND_COLLECT:
        // For 'all_click_and_collect', CVV cannot be skipped for any click and collect delivery choice.
        if (isClickAndCollectChoice(selectedDeliveryChoiceId)) {
          return false;
        }
        break;
      case cvvSkipVariants.NEW_CLICK_AND_COLLECT:
        // Require CVV for new collection points in click and collect deliveries
        if (isClickAndCollectChoice(selectedDeliveryChoiceId) && !isSelectedCollectionPointInSavedPoints) {
          return false;
        }
        break;
      case cvvSkipVariants.ALL_TRANSACTIONS:
        // For 'all_transactions', CVV can never be skipped.
        return false;
      default:
        // If there is no variant or the variant is unrecognized, proceed to the default behavior.
        break;
    }
  }

  if (isClickAndCollectChoice(selectedDeliveryChoiceId)) {
    return true;
  }

  const addressesExist = !isEmptyObject(selectedBillingAddress) && !isEmptyObject(confirmedDeliveryAddress);
  const addressesMatch = deepEqual(selectedBillingAddress, confirmedDeliveryAddress);

  if (addressesExist && addressesMatch) {
    return true;
  }

  return false;
}

export default function getSavedCardDetailsFormConfig({
  cardType,
  securityCodeLength,
  securityCodeOnly,
  selectedBillingAddress,
  confirmedDeliveryAddress,
  selectedDeliveryChoiceId,
  reduceSavedCardCvvSkipEnabled,
  reducedSavedCardCvvSkipVariant,
  isSelectedCollectionPointInSavedPoints,
  addressModified,
  billingAddressRecentlyUpdated,
  deliveryAddressRecentlyUpdated,
}) {
  const config = {
    id: SAVED_CARD_DETAILS_FORM_ID,
    fields: [],
  };

  if (
    canSkipCVV({
      cardType,
      selectedDeliveryChoiceId,
      selectedBillingAddress,
      confirmedDeliveryAddress,
      reduceSavedCardCvvSkipEnabled,
      reducedSavedCardCvvSkipVariant,
      isSelectedCollectionPointInSavedPoints,
      addressModified,
      billingAddressRecentlyUpdated,
      deliveryAddressRecentlyUpdated,
    })
  ) {
    return config;
  }

  const securityCodeField = {
    ...CARD_SECURITY_CODE,
    className: 'securityCodeField',
    inputWrapperClassName: getCvvClassName(cardType),
    minLength: securityCodeLength,
    maxLength: securityCodeLength,
    validationMessageKeys: {
      required:
        cardType === supportedCreditCardTypes.AMERICAN_EXPRESS
          ? 'Please enter the 4 digits on the front of your card above the long card number'
          : 'Please enter the last 3 digits found on the back of your card',
      minLength:
        cardType === supportedCreditCardTypes.AMERICAN_EXPRESS
          ? 'Please enter the 4 digits on the front of your card above the long card number'
          : 'Please enter the last 3 digits found on the back of your card',
    },
    labelHelperElement: <SecuredCardInputTooltip cardType={cardType} />,
  };

  if (securityCodeOnly) {
    config.fields = [...config.fields, securityCodeField];

    return config;
  }

  config.fields = [
    ...config.fields,
    {
      ...CARD_NUMBER,
      disabled: true,
      normalize:
        cardType === supportedCreditCardTypes.AMERICAN_EXPRESS ? formatCardNumberAmex : formatCardNumberDefault,
    },
    {
      ...CARD_NAME,
      disabled: true,
    },
    {
      ...CARD_EXPIRY_DATE,
      disabled: true,
      normalize: formatExpiryDate,
    },
    securityCodeField,
  ];

  return config;
}
